/**
 * ブランドスコープコンテキスト
 *
 * ヘッダのブランドスイッチャーが選んだブランド ID を全ページで共有する。選択は
 * localStorage に永続化し、マスタに存在しない保存値は全ブランドへフォールバックする。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business ブランドスイッチャーの選択状態とブランドマスタを全ページへ供給する React コンテキスト。
 * @graph-connects api [calls] brands.list によるブランドマスタ取得
 */
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useState, type JSX, type ReactNode } from 'react';
import type { CreativeBrand } from '@cortex/domain-creative/web';
import { orpc } from '../api/orpc.js';
import {
  ALL_BRANDS_SCOPE,
  BRAND_SCOPE_STORAGE_KEY,
  resolveStoredBrandScope,
} from '../lib/brand-scope.js';

/** ブランドスコープコンテキストの値。 */
export interface BrandScopeValue {
  /** 選択中のブランド ID ('' = 全ブランド)。 */
  scope: string;
  /** ブランドを切り替える。 */
  setScope: (scope: string) => void;
  /** ブランドマスタ (order 昇順)。 */
  brands: CreativeBrand[];
}

/** @graph-connects none */
const BrandScopeContext = createContext<BrandScopeValue>({
  scope: ALL_BRANDS_SCOPE,
  setScope: () => undefined,
  brands: [],
});

/**
 * ブランドスコーププロバイダ。
 * @param props children
 * @returns プロバイダ要素
 * @graph-connects api [calls] brands.list によるブランドマスタ取得
 */
export function BrandScopeProvider(props: { children: ReactNode }): JSX.Element {
  const brandsQuery = useQuery(orpc.brands.list.queryOptions({ input: {} }));
  const brands = useMemo(() => brandsQuery.data?.items ?? [], [brandsQuery.data]);
  const [rawScope, setRawScope] = useState<string>(
    () => localStorage.getItem(BRAND_SCOPE_STORAGE_KEY) ?? ALL_BRANDS_SCOPE,
  );

  const value = useMemo<BrandScopeValue>(() => {
    return {
      // マスタ確定前は保存値 (localStorage 生値) を検証できないため、削除済み ID を
      // 誤って API に送らないよう全ブランドへ倒す。確定後に保存値を解決して復元する。
      scope: brands.length === 0 ? ALL_BRANDS_SCOPE : resolveStoredBrandScope(rawScope, brands),
      setScope: (scope: string) => {
        setRawScope(scope);
        localStorage.setItem(BRAND_SCOPE_STORAGE_KEY, scope);
      },
      brands,
    };
  }, [rawScope, brands]);

  return <BrandScopeContext.Provider value={value}>{props.children}</BrandScopeContext.Provider>;
}

/**
 * ブランドスコープを参照するフック。
 * @returns ブランドスコープコンテキストの値
 * @graph-connects none
 */
export function useBrandScope(): BrandScopeValue {
  return useContext(BrandScopeContext);
}
